import { hasProperty, isEmpty } from "../helpers";
import { EMPTY_STRING, refFrames } from "../../constants";
import { SystematicTemplateNames } from "../../components/CreateTPlan_3Biopsy/constants";

export const hasMargins = (tplan) =>
    !isEmpty(tplan) &&
    hasProperty(tplan, "StructureData.Margins.length") &&
    tplan.StructureData.Margins.length > 0;

export const getMarginByIdx = (tplan, idx) => {
    if (hasMargins(tplan) && tplan.StructureData.Margins.length > idx) {
        return tplan.StructureData.Margins[Number(idx)];
    }
};

export const hasBxCores = (tplan) =>
    !isEmpty(tplan) &&
    hasProperty(tplan, "StructureData.Biopsy.length") &&
    tplan.StructureData.Biopsy.length > 0;

export const hasROIs = (tplan) =>
    !isEmpty(tplan) &&
    hasProperty(tplan, "StructureData.ROIs.length") &&
    tplan.StructureData.ROIs.length > 0;

export const getROIs = (tplan) => {
    if (hasROIs(tplan)) {
        return tplan.StructureData.ROIs;
    }
};

export const hasOtherStructures = (tplan) =>
    !isEmpty(tplan) &&
    hasProperty(tplan, "StructureData.OtherStructures.length") &&
    tplan.StructureData.OtherStructures.length > 0;

export const getOtherStructures = (tplan) => {
    if (hasOtherStructures(tplan)) {
        return tplan.StructureData.OtherStructures;
    }
};

export const hasCoreStructure = (tplan) =>
    !isEmpty(tplan) && hasProperty(tplan, "StructureData.CoreStructure");

export const getCoreStructure = (tplan) => {
    if (hasCoreStructure(tplan)) {
        return tplan.StructureData.CoreStructure;
    }
};

export const hasMRI = (tplan) => {
    if (tplan?.AdditionalData?.MRI?.length > 0) {
        return (
            Array.isArray(tplan.AdditionalData.MRI[0].Slices) &&
            tplan.AdditionalData.MRI[0].Slices.length > 0
        );
    }

    return false;
};

export const getMRI = (tplan) => {
    if (hasMRI(tplan)) {
        return tplan.AdditionalData.MRI;
    }
};

export const getMRIURIs = (tplan) => {
    if (hasMRI(tplan)) {
        return tplan.AdditionalData.MRI[0].Slices.map((slice) => slice.URI);
    }
};

export const getStructureInFrame = (structure, frame) => {
    if (hasProperty(structure, `Frame.${frame}`)) {
        return structure.Frame[frame.toString()];
    } else {
        return undefined;
    }
};

export const getBiopsyCores = (tplan) => tplan.StructureData.Biopsy;

export const hasCancerEstimationMap = (tplan) =>
    !isEmpty(tplan) &&
    hasProperty(tplan, "SavedData.CPMURI.length") &&
    tplan.SavedData.CPMURI.length > 0;

export const hasPatientData = (tplan) =>
    !isEmpty(tplan) && hasProperty(tplan, "PatientData");

export const getGleasonGradeGroup = (tplan) => {
    if (hasPatientData(tplan)) {
        return tplan.PatientData.GleasonGradeGroup;
    }
};

export const hasPlanCreationStep = (tplan) =>
    !isEmpty(tplan) && hasProperty(tplan, "SavedData.PlanCreationStep");

export const hasMetaData = (tplan) =>
    !isEmpty(tplan) && hasProperty(tplan, "MetaData");

export const getPlanCreationStep = (tplan) => {
    if (hasPlanCreationStep(tplan)) {
        return tplan.SavedData.PlanCreationStep;
    } else {
        return 0;
    }
};
export const isMRIOnly = (tplan) => {
    if (!isEmpty(tplan) && hasProperty(tplan, "SavedData.MRIOnlyPlan")) {
        return tplan.SavedData.MRIOnlyPlan;
    } else {
        return false;
    }
};

export const isKoelisBiopsy = (tplan) => {
    return (
        !isEmpty(tplan) &&
        hasProperty(tplan, "StructureData.StructureMetaData.BiopsyDevice") &&
        tplan.StructureData.StructureMetaData.BiopsyDevice === "KOELIS"
    );
};

// True when phi-parser ingested a Koelis case that already had an MRI-space
// segmentation (a `koelis_mri_sseg`), so it took the "No Fusion" path and
// never ran TRUS-to-MRI fusion or the AI segmentation pipeline for this plan
// (see phi-parser's koelis/case_folder.py). This is distinct from both:
//   - isMRIOnly: a manually uploaded/freehand plan with no biopsy device
//   - the AI/reverse-fusion path: AIProstateURI/SegmentationAIStatus present
// Callers that only check for those two cases will misclassify this plan.
export const isKoelisSegmentationProvidedWithoutFusion = (plan) => {
    if (isEmpty(plan) || !isKoelisBiopsy(plan.TreatmentPlan)) {
        return false;
    }

    const saveData = plan.SavedData || {};
    const hasAISegmentationPipeline =
        (saveData.AIProstateURI && saveData.AIProstateURI !== EMPTY_STRING) ||
        (saveData.AIEditedProstateURI &&
            saveData.AIEditedProstateURI !== EMPTY_STRING);

    if (hasAISegmentationPipeline) {
        return false;
    }

    const coreStructureMRIFrame = getStructureInFrame(
        getCoreStructure(plan.TreatmentPlan),
        refFrames.MRI
    );

    return !!(
        coreStructureMRIFrame && coreStructureMRIFrame.URI !== EMPTY_STRING
    );
};

export const addCoreStructureToPlan = (plan, mriStructParams) => {
    if (
        getStructureInFrame(getCoreStructure(plan.TreatmentPlan), refFrames.MRI)
    ) {
        return plan;
    }

    let coreStructure = {
        Frame: { MRI: mriStructParams },
    };

    plan.TreatmentPlan.StructureData["CoreStructure"] = coreStructure;

    return plan;
};

export const hasUserUploadedSegmentationURI = (tplan) =>
    !isEmpty(tplan) &&
    hasProperty(tplan, "SavedData.UploadedProstateURI.length") &&
    tplan.SavedData.UploadedProstateURI.length > 0;

export const hasPathologyReport = (tplan) =>
    !isEmpty(tplan) &&
    hasProperty(tplan, "SavedData.IsPathReportUploaded") &&
    tplan.SavedData.IsPathReportUploaded;

export const hasTemplate = (tplan) =>
    !isEmpty(tplan) &&
    hasProperty(tplan, "SavedData.IsTemplateUploaded") &&
    tplan.SavedData.IsTemplateUploaded;

export const get3DDataFolder = (tplan, patientUUID, visitUUID) => {
    let scanId = tplan.MetaData.MasterScanID;
    if (scanId) {
        return `${patientUUID}/${visitUUID}/${scanId}/3D_DATA`;
    } else {
        // No scan ID expected for freehand case
        return `${patientUUID}/${visitUUID}/3D_DATA`;
    }
};

export const getBxTemplate = (tplan) => {
    if (
        hasStructureMetData(tplan) &&
        tplan.StructureData.StructureMetaData.BiopsyTemplate
    ) {
        return tplan.StructureData.StructureMetaData.BiopsyTemplate;
    } else {
        return SystematicTemplateNames.TR_12; // Default to TR template for backwards compatibility
    }
};

const hasStructureMetData = (tplan) =>
    !isEmpty(tplan) &&
    hasProperty(tplan, "StructureData.StructureMetaData") &&
    tplan.StructureData.StructureMetaData;
